import { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "../../supabase.types";
import { fetchComedianVenueData } from "../../legacy/metrics";

export interface GeoStatePoint {
  state: string; // two-letter postal code, e.g. "TX"
  value: number; // reachable fans summed across the state's booked venues
}

export interface GeoCityPoint {
  city: string;
  state: string;
  lat: number;
  long: number;
  value: number; // reachable fans near this venue
}

export interface FansByGeo {
  states: GeoStatePoint[]; // choropleth shading
  cities: GeoCityPoint[]; // point markers at venue lat/long
}

/**
 * Resolves "fans near your venues" geography for one comedian via the
 * get_comedian_fans_summary RPC (reachable-fan counts per booked venue, by
 * city/state). Produces two layers for the map chart:
 *
 *  - states[]: per-state rollup (sum of reachable fans across that state's
 *    venues) for the choropleth.
 *  - cities[]: one dot per venue, positioned at the venue's lat/long. The RPC
 *    returns no coordinates, so we join venue lat/long from the comedian's show
 *    data (fetchComedianVenueData) keyed by venue id.
 *
 * Auth is enforced at the route layer; caller must already have authorized
 * comedianId.
 */
export async function fetchFansByGeo(
  supabase: SupabaseClient<Database>,
  comedianId: string
): Promise<FansByGeo> {
  const { data, error } = await supabase.rpc("get_comedian_fans_summary", {
    p_comedian_id: comedianId,
  });

  if (error) {
    throw new Error(
      `[chart-analytics] Failed to fetch fans-by-geo: ${error.message}`
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) return { states: [], cities: [] };

  // State rollup: sum reachable fans across every booked venue in each state.
  const byState = new Map<string, number>();
  for (const row of rows) {
    if (!row.state) continue;
    const postal = row.state.trim().toUpperCase();
    byState.set(
      postal,
      (byState.get(postal) ?? 0) + (row.users_within_distance ?? 0)
    );
  }
  const states = Array.from(byState.entries())
    .map(([state, value]) => ({ state, value }))
    .sort((a, b) => b.value - a.value);

  // City dots need lat/long, which the RPC omits — join from show venue data.
  // fetchComedianVenueData returns one row per show, so dedupe by venue id
  // (exposed as show_id, the venues table primary key).
  const venues = (await fetchComedianVenueData(comedianId, supabase)) ?? [];
  const latLongByVenue = new Map<string, { lat: number; long: number }>();
  for (const v of venues) {
    const loc = v.venue_location;
    if (
      !loc ||
      typeof loc.latitude !== "number" ||
      typeof loc.longitude !== "number"
    ) {
      continue;
    }
    latLongByVenue.set(String(v.show_id), {
      lat: loc.latitude,
      long: loc.longitude,
    });
  }

  const cities: GeoCityPoint[] = [];
  for (const row of rows) {
    const loc = latLongByVenue.get(String(row.venue_id));
    if (!loc) continue;
    cities.push({
      city: row.city,
      state: row.state ? row.state.trim().toUpperCase() : "",
      lat: loc.lat,
      long: loc.long,
      value: row.users_within_distance ?? 0,
    });
  }

  return { states, cities };
}
